import React, { useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Password } from "primereact/password";
import { Dropdown } from "primereact/dropdown";
import { Messages } from "primereact/messages";
import { Toast } from "primereact/toast";
import { BreadCrumb } from "primereact/breadcrumb";
import { BaseApiServiceImpl } from "../app_utils/api/BaseApiServiceImpl";
import { MessageUtils } from "../app_utils/utils/MessageUtils";
import { UserSessionUtils } from "../app_utils/utils/UserSessionUtils";
import { HOME_ROUTE_PATH } from "../app_utils/route_paths/resolver/PageRoutes";

const Profile = () => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [emailAddress, setEmailAddress] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [gender, setGender] = useState<number | null>(null);
  const [countryId, setCountryId] = useState<number | null>(null);
  const [countries, setCountries] = useState<any>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const message = useRef<any>();
  const toast = useRef<Toast>(null);

  const userId = UserSessionUtils.getUserDetails()?.id;

  const breadcrumbHome = {
    icon: "pi pi-home",
    command: () => {
      window.location.href = HOME_ROUTE_PATH;
    },
  };

  useEffect(() => {
    if (userId) {
      new BaseApiServiceImpl("/api/v1/users/me")
        .getRequestWithJsonResponse({})
        .then((response) => {
          const user = response?.data || response;
          setFirstName(user?.firstName || "");
          setLastName(user?.lastName || "");
          setEmailAddress(user?.emailAddress || "");
          setPhoneNumber(user?.phoneNumber || "");
          setGender(user?.genderId || user?.gender || null);
          setCountryId(user?.countryId || null);
          setIsLoading(false);
        })
        .catch(() => {
          setIsLoading(false);
        });

      new BaseApiServiceImpl("/api/v1/lookups/countries")
        .getRequestWithJsonResponse({ offset: 0, limit: 500 })
        .then((response) => {
          setCountries(response?.records || []);
        })
        .catch(() => {});
    }
  }, [userId]);

  const saveProfile = () => {
    if (!userId) return;

    setIsSaving(true);
    const userData: any = {
      id: userId,
      firstName,
      lastName,
      emailAddress,
      phoneNumber,
      genderId: gender || 0,
      countryId: countryId || 0,
      roleIds: [],
    };

    new BaseApiServiceImpl("/api/v1/users/" + userId)
      .putRequestWithJsonResponse(userData)
      .then((response) => {
        setIsSaving(false);
        MessageUtils.showSuccessMessage(message, "Profile updated successfully");
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: "Profile updated successfully",
        });
      })
      .catch((error) => {
        setIsSaving(false);
        MessageUtils.showErrorMessage(message, error.message);
      });
  };

  if (isLoading) {
    return <div className="p-m-4">Loading profile...</div>;
  }

  return (
    <div className="grid">
      <div className="col-12">
        <div className="card">
          <BreadCrumb home={breadcrumbHome} model={[{ label: "My Profile" }]} />
          <h5>My Profile</h5>
          <Messages ref={message} style={{ width: "100%" }} />
          <Toast ref={toast} />

          <div className="grid">
            <div className="col-12 md:col-6">
              <label htmlFor="firstName">First Name</label>
              <InputText
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="w-full"
              />
            </div>
            <div className="col-12 md:col-6">
              <label htmlFor="lastName">Last Name</label>
              <InputText
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="w-full"
              />
            </div>
            <div className="col-12 md:col-6">
              <label htmlFor="emailAddress">Email Address</label>
              <InputText
                id="emailAddress"
                value={emailAddress}
                onChange={(e) => setEmailAddress(e.target.value)}
                className="w-full"
              />
            </div>
            <div className="col-12 md:col-6">
              <label htmlFor="phoneNumber">Phone Number</label>
              <InputText
                id="phoneNumber"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                className="w-full"
              />
            </div>
            <div className="col-12 md:col-6">
              <label htmlFor="gender">Gender</label>
              <Dropdown
                id="gender"
                value={gender}
                options={[
                  { name: "Male", id: 0 },
                  { name: "Female", id: 1 },
                  { name: "Other", id: 2 },
                ]}
                optionValue="id"
                optionLabel="name"
                onChange={(e) => setGender(e.value)}
                placeholder="Select gender"
                className="w-full"
              />
            </div>
            <div className="col-12 md:col-6">
              <label htmlFor="country">Country</label>
              <Dropdown
                id="country"
                value={countryId}
                options={countries}
                optionValue="id"
                optionLabel="name"
                onChange={(e) => setCountryId(e.value)}
                placeholder="Select country"
                className="w-full"
              />
            </div>
          </div>

          <div className="mt-4">
            <Button
              label="Save Changes"
              icon="pi pi-check"
              className="p-button-success"
              onClick={saveProfile}
              loading={isSaving}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;